import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Lock, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { useAdminAuth } from "../contexts/AdminAuthContext";

const FADE_IN = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.4 } };
const ERROR_ANIM = { initial: { opacity: 0, y: -4 }, animate: { opacity: 1, y: 0 } };
const BTN_HOVER = { scale: 1.01 };
const BTN_TAP = { scale: 0.99 };

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");
    try {
      await login(password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-black">
      <motion.div
        {...FADE_IN}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 border border-white/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-5 h-5 text-white/30" />
          </div>
          <h1 className="text-xl font-bold text-white">Admin Access</h1>
          <p className="text-xs text-white/30 mt-2">AM Decor Signs &amp; Prints</p>
        </div>

        <form onSubmit={handleSubmit} className="border border-white/[0.08] bg-[#060606] p-6">
          <label className="text-[10px] text-white/30 uppercase tracking-[0.15em] block mb-2.5">
            Master Password
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/15" />
            <input
              data-testid="admin-password"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoFocus
              className="w-full h-12 bg-black border border-white/[0.12] text-white pl-11 pr-11 text-sm outline-none focus:border-white/30 transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPw((p) => !p)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/15 hover:text-white/40"
            >
              {showPw ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>

          {error && (
            <motion.div
              {...ERROR_ANIM}
              className="mt-3 p-3 border border-red-500/20 bg-red-500/[0.03] flex items-center gap-2"
            >
              <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
              <p className="text-[10px] text-red-400">{error}</p>
            </motion.div>
          )}

          <motion.button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading || !password.trim()}
            whileHover={BTN_HOVER}
            whileTap={BTN_TAP}
            className="w-full h-12 bg-white text-black text-[10px] font-bold tracking-widest uppercase mt-5 hover:bg-neutral-100 transition-all disabled:opacity-40"
          >
            {loading ? "Signing in..." : "Sign In"}
          </motion.button>
        </form>

        <p className="text-center text-[9px] text-white/15 mt-6">
          Protected area. Unauthorized access is prohibited.
        </p>
      </motion.div>
    </div>
  );
}
